import axios from 'axios';
import { API_URL } from '@/lib/api';

async function blobLooksLikePdf(blob) {
  if (!(blob instanceof Blob) || blob.size === 0) return false;
  try {
    const buf = await blob.slice(0, 5).arrayBuffer();
    return new TextDecoder().decode(buf).startsWith('%PDF');
  } catch {
    return false;
  }
}

async function errorDetailFromBlob(blob) {
  try {
    const text = await blob.text();
    const j = JSON.parse(text);
    if (j?.detail) return j.detail;
  } catch {
    /* ignore */
  }
  return 'Could not download receipt';
}

async function fetchReceiptBlob(orderId) {
  const res = await axios.get(`${API_URL}/orders/${orderId}/receipt`, {
    responseType: 'blob',
    validateStatus: () => true,
  });

  const blob = res.data;
  const type = (res.headers['content-type'] || '').toLowerCase();

  if (res.status >= 400) {
    throw new Error(await errorDetailFromBlob(blob));
  }

  const declaredPdf = type.includes('application/pdf');
  const ok = declaredPdf || (await blobLooksLikePdf(blob));
  if (!ok) {
    throw new Error(await errorDetailFromBlob(blob));
  }

  return blob;
}

function triggerBlobDownload(blob, orderNumber, orderId) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safe = String(orderNumber || orderId).replace(/[^\w.-]+/g, '_');
  a.href = url;
  a.download = `receipt-${safe}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Downloads PDF receipt for an order (admin-auth request).
 * @param {string} orderId
 * @param {string} [orderNumber] — used for filename
 * @param {{ tryGenerateOnFailure?: boolean }} [opts] — if true, runs generate-invoice then retries / opens hosted PDF
 */
export async function downloadOrderReceipt(orderId, orderNumber, opts = {}) {
  const { tryGenerateOnFailure = false } = opts;

  try {
    const blob = await fetchReceiptBlob(orderId);
    triggerBlobDownload(blob, orderNumber, orderId);
    return { mode: 'download' };
  } catch (firstErr) {
    if (!tryGenerateOnFailure) throw firstErr;

    let hostedUrl = null;
    try {
      const { data } = await axios.post(`${API_URL}/orders/${orderId}/generate-invoice`);
      hostedUrl = data?.invoiceUrl ?? data?.invoice_url ?? null;
    } catch {
      /* continue to retry stream or rethrow */
    }

    try {
      const blob = await fetchReceiptBlob(orderId);
      triggerBlobDownload(blob, orderNumber, orderId);
      return { mode: 'download' };
    } catch {
      if (hostedUrl) {
        window.open(hostedUrl, '_blank', 'noopener,noreferrer');
        return { mode: 'hosted' };
      }
    }

    throw firstErr;
  }
}
